import { useEffect, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { Loader2, ArrowLeft, Save } from 'lucide-react'
import { apiGet } from '../lib/api'

interface ClienteForm {
  name: string
  email: string
  phone: string
  doc_type: string
  doc_number: string
  discount_pct: number
  address_street: string
  address_city: string
  address_state: string
  address_country: string
  postal_code: string
  is_active: boolean
}

const empty: ClienteForm = {
  name: '',
  email: '',
  phone: '',
  doc_type: '',
  doc_number: '',
  discount_pct: 0,
  address_street: '',
  address_city: '',
  address_state: '',
  address_country: 'México',
  postal_code: '',
  is_active: true,
}

export function ClienteFormPage() {
  const { id } = useParams({ strict: false }) as { id: string }
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [form, setForm] = useState<ClienteForm>(empty)
  const [err, setErr] = useState<string | null>(null)

  const { data, isLoading } = useQuery<any>({
    queryKey: ['cliente', id],
    queryFn: () => apiGet(`/api/admin/clientes/${id}`),
  })

  useEffect(() => {
    if (data) {
      setForm({
        name: data.nombre || '',
        email: data.email || '',
        phone: data.telefono || '',
        doc_type: data.docType || '',
        doc_number: data.docNumero || '',
        discount_pct: Number(data.descuento) || 0,
        address_street: data.direccion || '',
        address_city: data.ciudad || '',
        address_state: data.provincia || '',
        address_country: data.pais || 'México',
        postal_code: data.cp || '',
        is_active: !!data.activo,
      })
    }
  }, [data])

  const save = useMutation({
    mutationFn: async (payload: ClienteForm) => {
      const r = await fetch(`/api/admin/clientes/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const j = await r.json()
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`)
      return j
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cliente', id] })
      qc.invalidateQueries({ queryKey: ['clientes'] })
      navigate({ to: '/clientes/$id', params: { id } })
    },
    onError: (e: Error) => setErr(e.message),
  })

  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErr(null)
    save.mutate(form)
  }

  function set<K extends keyof ClienteForm>(k: K, v: ClienteForm[K]) {
    setForm((f) => ({ ...f, [k]: v }))
  }

  return (
    <>
      <Topbar
        title={`Editar cliente`}
        subtitle={data?.email}
        actions={
          <Link
            to="/clientes/$id"
            params={{ id }}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
          >
            <ArrowLeft className="size-3.5" /> Cancelar
          </Link>
        }
      />

      <div className="p-6">
        {isLoading && (
          <div className="grid place-items-center py-20 text-ink-500">
            <Loader2 className="size-8 animate-spin" />
          </div>
        )}

        {!isLoading && (
          <form onSubmit={onSubmit} className="mx-auto max-w-3xl space-y-6">
            {err && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {err}
              </div>
            )}

            <Section title="Datos personales">
              <Field label="Nombre completo *">
                <input
                  required
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </Field>
              <Field label="Email *">
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </Field>
              <Field label="Teléfono">
                <input
                  value={form.phone}
                  onChange={(e) => set('phone', e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </Field>
              <Field label="Descuento (%)">
                <input
                  type="number"
                  min={0}
                  max={100}
                  step="0.01"
                  value={form.discount_pct}
                  onChange={(e) => set('discount_pct', Number(e.target.value))}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </Field>
              <Field label="Tipo de documento">
                <input
                  value={form.doc_type}
                  onChange={(e) => set('doc_type', e.target.value)}
                  placeholder="RFC, CURP, etc."
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </Field>
              <Field label="Número de documento">
                <input
                  value={form.doc_number}
                  onChange={(e) => set('doc_number', e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 font-mono text-xs outline-none focus:border-brand-500"
                />
              </Field>
            </Section>

            <Section title="Dirección">
              <Field label="Calle y número" full>
                <input
                  value={form.address_street}
                  onChange={(e) => set('address_street', e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </Field>
              <Field label="Ciudad">
                <input
                  value={form.address_city}
                  onChange={(e) => set('address_city', e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </Field>
              <Field label="Estado">
                <input
                  value={form.address_state}
                  onChange={(e) => set('address_state', e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </Field>
              <Field label="Código postal">
                <input
                  value={form.postal_code}
                  onChange={(e) => set('postal_code', e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </Field>
              <Field label="País">
                <input
                  value={form.address_country}
                  onChange={(e) => set('address_country', e.target.value)}
                  className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
                />
              </Field>
            </Section>

            <Section title="Estado">
              <label className="col-span-2 inline-flex items-center gap-2 text-sm text-ink-700">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => set('is_active', e.target.checked)}
                  className="size-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
                />
                Cliente activo
              </label>
            </Section>

            <div className="flex justify-end gap-2">
              <Link
                to="/clientes/$id"
                params={{ id }}
                className="rounded-lg border border-ink-200 bg-white px-4 py-2 text-sm font-medium text-ink-700 hover:bg-ink-100"
              >
                Cancelar
              </Link>
              <button
                type="submit"
                disabled={save.isPending}
                className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
              >
                {save.isPending ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <Save className="size-3.5" />
                )}
                Guardar cambios
              </button>
            </div>
          </form>
        )}
      </div>
    </>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-ink-200 bg-white p-5">
      <h2 className="mb-4 font-semibold text-ink-900">{title}</h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">{children}</div>
    </div>
  )
}

function Field({
  label,
  children,
  full,
}: {
  label: string
  children: React.ReactNode
  full?: boolean
}) {
  return (
    <div className={full ? 'sm:col-span-2' : ''}>
      <label className="mb-1 block text-xs font-semibold text-ink-700">{label}</label>
      {children}
    </div>
  )
}
